// UltraFlex - PT Hub (Dashboard)
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, FileText, CreditCard, QrCode, MapPin, Shield, Clock, AlertTriangle, CheckCircle, XCircle, LogOut, Upload, Briefcase, RefreshCw } from 'lucide-react';
import WalletButtons from '../../components/WalletButtons';
import { useAuth } from '../../contexts/AuthContext';
import QRCode from 'react-qr-code';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PTHub() {
  const { user, token, logout, isPT } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    const storedToken = token || localStorage.getItem('uf_token');
    if (!storedToken) { navigate('/pt/login'); return; }
    fetchProfile(storedToken);
  }, [token]);

  const fetchProfile = async (authToken) => {
    const t = authToken || token || localStorage.getItem('uf_token');
    try {
      const resp = await fetch(`${API}/pt/me`, { headers: { 'Authorization': `Bearer ${t}` } });
      if (resp.ok) setProfile(await resp.json());
      else if (resp.status === 403 || resp.status === 401) navigate('/pt/login');
    } catch { navigate('/pt/login'); }
    setLoading(false);
  };

  const fetchQR = async () => {
    try {
      const resp = await fetch(`${API}/pt/qr-token`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (resp.ok) setQrData(await resp.json());
    } catch {}
  };

  const handleDocUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', docType);
    formData.append('description', file.name);
    try {
      await fetch(`${API}/pt/documents`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
      fetchProfile();
    } catch {}
  };

  const statusConfig = {
    draft: { color: 'text-zinc-400', bg: 'bg-zinc-800', icon: Clock, label: 'Draft' },
    submitted: { color: 'text-amber-400', bg: 'bg-amber-500/10', icon: Clock, label: 'Under Review' },
    pending_review: { color: 'text-amber-400', bg: 'bg-amber-500/10', icon: Clock, label: 'Pending Review' },
    approved: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: CheckCircle, label: 'Approved' },
    active: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: CheckCircle, label: 'Active' },
    rejected: { color: 'text-red-400', bg: 'bg-red-500/10', icon: XCircle, label: 'Rejected' },
    suspended: { color: 'text-red-400', bg: 'bg-red-500/10', icon: AlertTriangle, label: 'Suspended' },
    expired: { color: 'text-orange-400', bg: 'bg-orange-500/10', icon: AlertTriangle, label: 'Expired' },
  };

  if (loading) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><div className="text-zinc-400">Loading...</div></div>;
  if (!profile) return null;

  const sc = statusConfig[profile.status] || statusConfig.draft;
  const StatusIcon = sc.icon;

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="pt-hub-page">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-800">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center text-white font-bold">{profile.first_name?.[0]}{profile.last_name?.[0]}</div>
            <div>
              <p className="text-white font-semibold text-sm">{profile.first_name} {profile.last_name}</p>
              <p className="text-zinc-500 text-xs">{profile.pt_number} &middot; Personal Trainer</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className={`${sc.bg} ${sc.color} px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1`} data-testid="pt-status-badge"><StatusIcon className="w-3 h-3" /> {sc.label}</div>
            <button onClick={() => { logout(); navigate('/'); }} className="text-zinc-500 hover:text-white" data-testid="pt-logout"><LogOut className="w-5 h-5" /></button>
          </div>
        </div>
      </div>

      {/* Status Alert */}
      {profile.status === 'draft' && (
        <div className="bg-amber-500/10 border-b border-amber-500/20 px-4 py-3"><div className="max-w-5xl mx-auto flex items-center gap-2 text-amber-400 text-sm"><AlertTriangle className="w-4 h-4" /> Your application is still in draft. Upload required documents and submit for review.</div></div>
      )}
      {profile.status === 'submitted' && (
        <div className="bg-blue-500/10 border-b border-blue-500/20 px-4 py-3"><div className="max-w-5xl mx-auto flex items-center gap-2 text-blue-400 text-sm"><Clock className="w-4 h-4" /> Your application is being reviewed by management. You'll be notified once approved.</div></div>
      )}
      {profile.status === 'rejected' && (
        <div className="bg-red-500/10 border-b border-red-500/20 px-4 py-3"><div className="max-w-5xl mx-auto text-red-400 text-sm"><XCircle className="w-4 h-4 inline mr-1" /> Application rejected: {profile.rejection_reason || 'Contact management for details.'}</div></div>
      )}
      {profile.status === 'suspended' && (
        <div className="bg-red-500/10 border-b border-red-500/20 px-4 py-3"><div className="max-w-5xl mx-auto text-red-400 text-sm"><AlertTriangle className="w-4 h-4 inline mr-1" /> Your account is suspended. Contact management to resolve.</div></div>
      )}

      {/* Tabs */}
      <div className="max-w-5xl mx-auto px-4 mt-6">
        <div className="flex gap-1 bg-zinc-900 rounded-xl p-1 mb-6">
          {[{ id: 'overview', label: 'Overview', icon: User }, { id: 'documents', label: 'Documents', icon: FileText }, { id: 'billing', label: 'Billing', icon: CreditCard }, ...(profile.status === 'active' ? [{ id: 'access', label: 'Access', icon: QrCode }] : [])].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`pt-tab-${t.id}`} className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${tab === t.id ? 'bg-red-600 text-white' : 'text-zinc-400 hover:text-white'}`}><t.icon className="w-4 h-4" /> {t.label}</button>
          ))}
        </div>

        {/* Overview Tab */}
        {tab === 'overview' && (
          <div className="space-y-4" data-testid="pt-tab-overview-content">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card title="Account Status"><StatusIcon className={`w-6 h-6 ${sc.color}`} /><p className={`text-lg font-bold ${sc.color}`}>{sc.label}</p></Card>
              <Card title="Commercial Model"><Briefcase className="w-6 h-6 text-blue-400" /><p className="text-lg font-bold text-white capitalize">{profile.commercial_model === 'monthly_rent' ? 'Monthly Rent' : 'Pay Per Session'}</p>{profile.monthly_rate && <p className="text-zinc-500 text-xs">£{profile.monthly_rate}/month</p>}{profile.session_rate && <p className="text-zinc-500 text-xs">£{profile.session_rate}/session</p>}</Card>
              <Card title="Home Gym"><MapPin className="w-6 h-6 text-red-400" /><p className="text-lg font-bold text-white">{profile.home_site_name || 'Not assigned'}</p>{profile.permitted_site_ids?.length > 1 && <p className="text-zinc-500 text-xs">{profile.permitted_site_ids.length} sites permitted</p>}</Card>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
              <h3 className="text-white font-semibold mb-3">Profile Details</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="Email" value={profile.email} /><Info label="Phone" value={profile.phone} />
                <Info label="City" value={profile.city} /><Info label="Postcode" value={profile.postcode} />
                <Info label="Insurance" value={profile.insurance_provider} /><Info label="Policy #" value={profile.insurance_policy_number} />
                <Info label="Insurance Expiry" value={profile.insurance_expiry} /><Info label="Documents" value={`${profile.document_count} uploaded`} />
              </div>
              {profile.specialisations?.length > 0 && (
                <div className="mt-3"><p className="text-zinc-500 text-xs mb-1">Specialisations</p><div className="flex flex-wrap gap-1">{profile.specialisations.map(s => <span key={s} className="px-2 py-0.5 bg-red-600/20 text-red-400 rounded-full text-xs">{s}</span>)}</div></div>
              )}
            </div>
            {profile.status !== 'active' && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="pt-qr-pending-notice">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><QrCode className="w-5 h-5 text-zinc-500" /> Access QR Code</h3>
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-zinc-800 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <QrCode className="w-8 h-8 text-zinc-600" />
                  </div>
                  <p className="text-zinc-400 text-sm">Your QR code will be available once your application is <strong className="text-white">approved and active</strong>.</p>
                  <p className="text-zinc-500 text-xs mt-2">Current status: <span className={sc.color}>{sc.label}</span></p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Documents Tab */}
        {tab === 'documents' && (
          <div className="space-y-4" data-testid="pt-tab-documents-content">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
              <h3 className="text-white font-semibold mb-3">Your Documents</h3>
              {profile.documents?.length === 0 ? <p className="text-zinc-500 text-sm">No documents uploaded yet.</p> : (
                <div className="space-y-2">{profile.documents.map(d => (
                  <div key={d.id} className="flex items-center justify-between bg-zinc-800 rounded-lg p-3">
                    <div><p className="text-white text-sm">{d.file_name}</p><p className="text-zinc-500 text-xs">{d.document_type.replace('_', ' ')} &middot; {d.review_status}</p></div>
                    <span className={`text-xs px-2 py-1 rounded ${d.review_status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' : d.review_status === 'rejected' ? 'bg-red-500/20 text-red-400' : 'bg-zinc-700 text-zinc-400'}`}>{d.review_status}</span>
                  </div>
                ))}</div>
              )}
            </div>
            {(profile.status === 'draft' || profile.status === 'rejected' || profile.status === 'submitted' || profile.status === 'active') && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
                <h3 className="text-white font-semibold mb-3">Upload Document</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {['qualification_cert', 'insurance_cert', 'first_aid', 'id_proof'].map(dt => (
                    <label key={dt} className="cursor-pointer bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg p-4 text-center transition-colors" data-testid={`pt-hub-upload-${dt}`}>
                      <Upload className="w-5 h-5 text-zinc-400 mx-auto mb-1" />
                      <p className="text-white text-xs font-medium capitalize">{dt.replace(/_/g, ' ')}</p>
                      <input type="file" className="hidden" onChange={e => handleDocUpload(e, dt)} />
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Billing Tab */}
        {tab === 'billing' && (
          <BillingTab profile={profile} token={token} />
        )}

        {/* Access Tab */}
        {tab === 'access' && profile.status === 'active' && (
          <div className="space-y-4" data-testid="pt-tab-access-content">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 text-center">
              <h3 className="text-white font-semibold mb-4">Gym Access QR Code</h3>
              {qrData ? (
                <div>
                  <div className="bg-white rounded-xl p-4 inline-block mb-3"><QRCode value={qrData.qr_data} size={200} /></div>
                  <p className="text-zinc-500 text-xs">Expires in {qrData.expires_in_seconds}s</p>
                  <button onClick={fetchQR} className="mt-3 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm rounded-lg flex items-center gap-1 mx-auto" data-testid="pt-refresh-qr"><RefreshCw className="w-3 h-3" /> Refresh</button>
                </div>
              ) : (
                <button onClick={fetchQR} className="px-6 py-3 bg-red-600 hover:bg-red-500 text-white rounded-xl font-semibold transition-colors" data-testid="pt-generate-qr">Generate QR Code</button>
              )}
              <p className="text-zinc-600 text-xs mt-3">Your PT number: <span className="text-white font-mono">{profile.pt_number}</span></p>
            </div>
            {/* Digital Wallet Pass */}
            {profile.id && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="pt-wallet-section">
                <WalletButtons passType="pt" entityId={profile.id} showPreview={true} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const Card = ({ title, children }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4"><p className="text-zinc-500 text-xs mb-2">{title}</p><div className="flex items-center gap-2">{children}</div></div>
);

const Info = ({ label, value }) => (
  <div><p className="text-zinc-500 text-xs">{label}</p><p className="text-white text-sm">{value || '-'}</p></div>
);

const BillingTab = ({ profile, token }) => {
  const [cardStatus, setCardStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingCard, setLoadingCard] = useState(true);
  const [settingUp, setSettingUp] = useState(false);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    fetchCardStatus();
    fetchHistory();
    const params = new URLSearchParams(window.location.search);
    if (params.get('card_setup') === 'success') {
      setTimeout(fetchCardStatus, 2000);
    }
  }, []);

  const fetchCardStatus = async () => {
    try {
      const resp = await fetch(`${API}/pt/card-status`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (resp.ok) setCardStatus(await resp.json());
    } catch {}
    setLoadingCard(false);
  };

  const fetchHistory = async () => {
    try {
      const resp = await fetch(`${API}/pt/billing-history`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (resp.ok) { const data = await resp.json(); setHistory(data.items || []); }
    } catch {}
  };

  const handleSetupCard = async () => {
    setSettingUp(true);
    try {
      const resp = await fetch(`${API}/pt/setup-card`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.checkout_url) window.location.href = data.checkout_url;
        else alert('Card setup is not available at this time. Stripe integration may not be configured.');
      } else {
        const err = await resp.json().catch(() => ({}));
        alert(err.detail || 'Card setup is not available at this time. Please contact management.');
      }
    } catch { alert('Unable to connect to payment service. Please try again later.'); }
    setSettingUp(false);
  };

  const handleRemoveCard = async () => {
    if (!window.confirm('Are you sure you want to remove your saved card?')) return;
    setRemoving(true);
    try {
      const resp = await fetch(`${API}/pt/remove-card`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      if (resp.ok) { setCardStatus({ has_card: false, card: null }); }
    } catch {}
    setRemoving(false);
  };

  const setupSuccess = new URLSearchParams(window.location.search).get('card_setup') === 'success';

  return (
    <div className="space-y-4" data-testid="pt-tab-billing-content">
      {setupSuccess && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 flex items-center gap-2 text-emerald-400 text-sm" data-testid="card-setup-success">
          <CheckCircle className="w-4 h-4" /> Card saved successfully! You're all set for session billing.
        </div>
      )}

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <h3 className="text-white font-semibold mb-3">Billing Overview</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Info label="Commercial Model" value={profile.commercial_model === 'monthly_rent' ? 'Monthly Rent' : 'Pay Per Session'} />
          <Info label="Billing Status" value={profile.billing_status || 'N/A'} />
          {profile.monthly_rate && <Info label="Monthly Rate" value={`£${profile.monthly_rate}`} />}
          {profile.session_rate && <Info label="Session Rate" value={`£${profile.session_rate}`} />}
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold">Saved Card</h3>
          <CreditCard className="w-5 h-5 text-zinc-500" />
        </div>

        {loadingCard ? (
          <div className="text-zinc-500 text-sm">Loading card info...</div>
        ) : cardStatus?.has_card ? (
          <div>
            <div className="flex items-center gap-4 bg-zinc-800 rounded-lg p-4" data-testid="saved-card-info">
              <div className="w-12 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white text-sm font-medium capitalize">{cardStatus.card.brand} ending in {cardStatus.card.last4}</p>
                <p className="text-zinc-500 text-xs">Expires {cardStatus.card.exp_month}/{cardStatus.card.exp_year}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={handleSetupCard} disabled={settingUp} className="px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-white text-xs rounded-lg transition-colors" data-testid="update-card-btn">
                  {settingUp ? 'Redirecting...' : 'Update'}
                </button>
                <button onClick={handleRemoveCard} disabled={removing} className="px-3 py-1.5 bg-red-600/20 hover:bg-red-600/30 text-red-400 text-xs rounded-lg transition-colors" data-testid="remove-card-btn">
                  {removing ? 'Removing...' : 'Remove'}
                </button>
              </div>
            </div>
            <p className="text-zinc-600 text-xs mt-2">This card will be charged per session by UltraFlex management.</p>
          </div>
        ) : (
          <div className="text-center py-6">
            <CreditCard className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm mb-1">No card saved</p>
            <p className="text-zinc-600 text-xs mb-4">Add a card to enable per-session billing.</p>
            <button onClick={handleSetupCard} disabled={settingUp} className="px-6 py-2.5 bg-red-600 hover:bg-red-500 text-white rounded-xl font-semibold text-sm transition-colors" data-testid="setup-card-btn">
              {settingUp ? 'Redirecting to Stripe...' : 'Add Card via Stripe'}
            </button>
          </div>
        )}
      </div>

      {history.length > 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-3">Payment History</h3>
          <div className="space-y-2">
            {history.map(h => (
              <div key={h.id} className="flex items-center justify-between bg-zinc-800 rounded-lg p-3" data-testid={`billing-item-${h.id}`}>
                <div>
                  <p className="text-white text-sm">{h.description}</p>
                  <p className="text-zinc-500 text-xs">{new Date(h.created).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                </div>
                <div className="text-right">
                  <p className="text-white font-medium">£{h.amount.toFixed(2)}</p>
                  <span className={`text-xs px-2 py-0.5 rounded ${h.status === 'succeeded' ? 'bg-emerald-500/20 text-emerald-400' : h.status === 'failed' ? 'bg-red-500/20 text-red-400' : 'bg-zinc-700 text-zinc-400'}`}>{h.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="billing-history-empty">
          <h3 className="text-white font-semibold mb-3">Payment History</h3>
          <p className="text-zinc-500 text-sm text-center py-4">No billing history yet. Charges will appear here once sessions are billed.</p>
        </div>
      )}
    </div>
  );
};
